<template>
    <v-dialog v-model="dialog" persistent class="error-dialog">
        <template v-if="hasActivator" v-slot:activator="{ props }">
            <slot :activator="props" name="actions">
                <v-btn v-bind="props" color="primary" dark small depressed>
                    {{ toSentenceCase($t(buttonText)) }}
                </v-btn>
            </slot>
        </template>
        <v-card class="error-dialog__container">
            <div class="error-dialog__header">
                <span>{{ headerText }}</span>
                <v-btn icon @click="closeDialog">
                    <icon icon-name="custom:cancel" small />
                </v-btn>
            </div>
            <v-divider class="error-dialog__header-divider" />
            <div>
                <div v-if="size(formattingErrors)" class="error-dialog__formatting-errors">
                    <icon icon-name="custom:warning" />
                    <span class="error-dialog__formatting-errors--upload-failed">
                        {{ $t('planning.promotionsMaintenance.funding.errors.uploadFailed') }}:
                    </span>
                    {{ $t('planning.promotionsMaintenance.funding.errors.formattingErrors') }}
                </div>
                <vuex-treeview
                    v-else
                    :items="errorTree || treeViewErrors"
                    density="compact"
                    :disable-hover="true"
                />
            </div>
            <div class="error-dialog__close-button">
                <v-btn primary depressed @click="closeDialog">
                    {{ toSentenceCase($t('actions.close')) }}
                </v-btn>
            </div>
        </v-card>
    </v-dialog>
</template>

<script>
import { size, pick, omit, keys } from 'lodash';

export default {
    props: {
        errors: {
            type: Object,
            default: null,
        },
        hasActivator: {
            type: Boolean,
            default: false,
        },
        errorTree: {
            type: Array,
            default: null,
        },
        header: {
            type: String,
            default: '',
        },
        buttonText: String,
    },
    emits: ['close'],
    data: () => ({
        dialog: false,
    }),

    computed: {
        treeViewErrors() {
            return keys(omit(this.errors, ['formattingErrors'])).reduce((acc, key, index) => {
                acc.push({
                    id: index,
                    title: `${this.$t(
                        `planning.promotionsMaintenance.funding.errors.${key}`
                    )} - ${this.$t(`planning.promotionsMaintenance.funding.total`)}: ${size(
                        this.errors[key]
                    )}`,
                    children: this.errors[key].map((pKey, ix) => {
                        return {
                            id: ix,
                            title: pKey,
                        };
                    }),
                });
                return acc;
            }, []);
        },

        formattingErrors() {
            return pick(this.errors, ['formattingErrors']);
        },

        headerText() {
            if (this.header) {
                return this.toSentenceCase(this.$t(this.header));
            }
            return this.$t(`planning.promotionsMaintenance.funding.fileUploadReport`);
        },
    },

    methods: {
        size,

        close() {
            this.dialog = false;
        },
        open() {
            this.dialog = true;
        },
        closeDialog() {
            this.$emit('close');

            if (!this.preventDefault) {
                this.close();
            }
        },
    },
};
</script>

<style scoped lang="scss">
@import '@style/base/_variables.scss';
.error-dialog {
    &__header {
        display: flex;
        justify-content: space-between;
        font-size: 1.6rem;
        font-weight: 600;
        margin-left: 1rem;
        margin-right: 1rem;
        padding-bottom: 0.5rem;
        padding-top: 0.5rem;
    }

    &__header-divider {
        margin-left: 1rem;
        margin-right: 1rem;
        padding-bottom: 1rem;
    }

    &__container {
        width: 60rem;
        border-left: 0.4rem solid $promo-primary-colour;
        font-size: 1.3rem;
    }

    &__formatting-errors {
        height: 5rem;
        padding-left: 0.5rem;
        padding-top: 1rem;
        padding-bottom: 1rem;

        &--upload-failed {
            font-weight: 600;
        }
    }

    &__close-button {
        display: flex;
        align-items: flex-end;
        padding-bottom: 0.5rem;
        padding-top: 0.5rem;
        padding-right: 1rem;

        .v-btn {
            margin-left: auto;
        }
    }
}

:deep(.v-treeview-node__root) {
    min-height: 2rem;
}

:deep(.v-list-group__items) {
    .v-list-item:nth-child(even) {
        background-color: $promo-table-blue-bg-colour-2;
    }
}

:deep(.v-list-item .v-list-item-title) {
    font-size: 1.3rem;
}

:deep(.v-list-group__items) {
    overflow-y: auto;
    max-height: 15rem;
}

:deep(.v-list-item) {
    padding-top: 0.3rem;
    padding-bottom: 0.3rem;
}
</style>
